import { IndexCheckResult } from '@/types/amazonIndexChecker';

export type ExportFormat = 'txt' | 'md' | 'csv';

export const exportResults = (results: IndexCheckResult[], format: ExportFormat, filename?: string) => {
  let content: string;
  let mimeType: string;
  let extension: string;

  switch (format) {
    case 'txt':
      content = generateTextExport(results);
      mimeType = 'text/plain';
      extension = 'txt';
      break;
    case 'md':
      content = generateMarkdownExport(results);
      mimeType = 'text/markdown';
      extension = 'md';
      break;
    case 'csv':
      content = generateCsvExport(results);
      mimeType = 'text/csv';
      extension = 'csv';
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }

  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `amazon-index-results.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

const generateTextExport = (results: IndexCheckResult[]): string => {
  const lines = results.map(result => {
    const status = result.isIndexed ? 'Indexed' : 'Not Indexed';
    const position = result.isIndexed && result.position ? ` (Position #${result.position})` : '';
    const date = formatExportDate(result.timestamp);
    return `${result.term} - ${status}${position} - ${date}`;
  });
  
  return [
    'Amazon Index Checker Results',
    '================================',
    `Total Results: ${results.length}`,
    `Exported on: ${new Date().toLocaleString()}`,
    '',
    ...lines
  ].join('\n');
};

const generateMarkdownExport = (results: IndexCheckResult[]): string => {
  const header = [
    '# Amazon Index Checker Results',
    '',
    `**Total Results:** ${results.length}`,
    `**Exported on:** ${new Date().toLocaleString()}`,
    '',
    '| Term | Status | Position | Date Checked |',
    '|------|--------|----------|--------------|'
  ];
  
  const rows = results.map(result => {
    const status = result.isIndexed ? 'Indexed' : 'Not Indexed';
    const position = result.isIndexed && result.position ? `#${result.position}` : '-';
    const date = formatExportDate(result.timestamp);
    return `| ${result.term} | ${status} | ${position} | ${date} |`;
  });
  
  return [...header, ...rows].join('\n');
};

const generateCsvExport = (results: IndexCheckResult[]): string => {
  const header = 'Term,Status,Date Checked';
  
  const rows = results.map(result => {
    const status = result.isIndexed ? 'Indexed' : 'Not Indexed';
    const date = formatExportDate(result.timestamp);
    // Escape CSV values that might contain commas
    const escapedTerm = `"${result.term.replace(/"/g, '""')}"`;
    return `${escapedTerm},"${status}","${date}"`;
  });
  
  return [header, ...rows].join('\n');
};

const formatExportDate = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};